import { Button, Typography } from '@breetometer/components';
import { ScannerAddressIcon, ScannerSavedAddressIcon } from 'BreetAssets';
import type { IDIALOGVERIFYTRANSACTIONPROPS } from 'BreetComponents';
import { type NetworkType, showToast, type TokenType } from 'BreetHelpers';
import { useModalApi, useSavedWalletAddressApi } from 'BreetHooks';
import { memo, useCallback, useMemo } from 'react';

import type { IWalletAddressFormCtaProps } from './WalletAddressForm.helpers';

const WalletAddressFormCtaComponent = ({
	formDataContextProps,
	resetWalletAddressProps,
	onScanWalletAddress,
	onSavedWalletAddress,
	variant = 'default',
}: IWalletAddressFormCtaProps) => {
	const { isSavedWalletAddressesEmpty, getIsWalletAddressSaved } = useSavedWalletAddressApi();

	const { openModalDirect } = useModalApi();

	const {
		hookFormProps: {
			watch,
			handleSubmit,
			formState: { errors },
		},
		isFormCompleted,
	} = formDataContextProps;

	const formData = watch();
	const selectedToken = formData.token as TokenType | undefined;
	const selectedNetwork = formData.network as NetworkType | undefined;
	const receiverAddress = formData.receiverAddress as string | undefined;
	const walletName = formData.walletName as string | undefined;

	const handleScanAddress = useCallback(() => {
		if (!selectedToken || !selectedNetwork) {
			showToast('error', `Please select${!selectedToken ? ' coin and' : ''} destination network`);
			return;
		}

		onScanWalletAddress();
	}, [onScanWalletAddress, selectedNetwork, selectedToken]);

	const isWalletAddressSaved = useMemo(
		() => getIsWalletAddressSaved(selectedToken, receiverAddress, walletName),
		[getIsWalletAddressSaved, receiverAddress, selectedToken, walletName]
	);

	const handleWalletAddress = useCallback(() => {
		openModalDirect({
			id: 'verifyTransaction',
			variant: 'right-sheet',
			metadata: {
				saveWalletAddressReqData: {
					address: receiverAddress ?? '',
					token: selectedToken as unknown as TokenType,
					network: selectedNetwork as unknown as NetworkType,
					label: walletName ?? '',
				},
				transactionType: 'saveWalletAddress',
				skipPin: true,
			} as IDIALOGVERIFYTRANSACTIONPROPS,
		});
	}, [receiverAddress, openModalDirect, selectedNetwork, selectedToken, walletName]);

	const handleCtaClick = useCallback(() => {
		if (isWalletAddressSaved) {
			onSavedWalletAddress();
		} else {
			handleWalletAddress();
		}
	}, [handleWalletAddress, isWalletAddressSaved, onSavedWalletAddress]);

	const onSelectAnotherAddress = useCallback(() => {
		resetWalletAddressProps();
	}, [resetWalletAddressProps]);

	const btnTitle = variant === 'default' ? 'Continue' : 'Validate Address';

	const isWalletAddressFormCompleted = !!selectedToken && !!selectedNetwork && !!receiverAddress && !!walletName;

	const isWalletErrors = Object.entries(errors).some(([key, value]) => {
		const isExcluded = key === 'amount' || key === 'root';
		return !isExcluded && !!value.message;
	});

	const disableBtn = variant === 'default' ? !isFormCompleted : !isWalletAddressFormCompleted || isWalletErrors;

	return (
		<>
			<div className='payment-request-form_usdDestination_send-crypto-alternative'>
				<Typography className='color-primary-100'>- Or -</Typography>

				<div className='payment-request-form_usdDestination_send-crypto-alternative_cta'>
					{!isSavedWalletAddressesEmpty && variant === 'default' && (
						<Button
							label='Use Another Address'
							variant='native'
							leadingIcon={<ScannerSavedAddressIcon className='svg-dark svg-dark-outline' />}
							type='button'
							className='color-primary-50'
							onClick={onSelectAnotherAddress}
						/>
					)}

					<Button
						label='Scan an Address'
						variant='native'
						leadingIcon={<ScannerAddressIcon className='svg-dark svg-dark-outline' />}
						type='button'
						className='color-primary-50'
						onClick={handleScanAddress}
					/>
				</div>
			</div>

			<div className='payment-request-form_usdDestination_send-crypto-cta'>
				<Button
					label={btnTitle}
					type='button'
					onClick={variant === 'default' ? handleSubmit(handleCtaClick) : handleCtaClick}
					disabled={disableBtn}
					className='primary-hover'
				/>
			</div>
		</>
	);
};

const WalletAddressFormCta = memo(WalletAddressFormCtaComponent);

WalletAddressFormCta.displayName = 'WalletAddressFormCta';

export default WalletAddressFormCta;
